if (!customElements.get('article-content')) {
  customElements.define('article-content', class ArticleContent extends HTMLElement {
    constructor() {
      super();
      this.tocItems = [];
      this.activeId = null;
      this.scrollHandler = null;
      this.mql = null;
      this.onBreakpointChange = null;
    }

    connectedCallback() {
      this.collectItems();
      this.buildTOC();
      this.initScrollTracking();
    }

    disconnectedCallback() {
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.mql && this.onBreakpointChange) this.mql.removeEventListener('change', this.onBreakpointChange);
    }

    headerHeight() {
      return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
    }

    // On mobile the aside is sticky above the content — offset by header + aside height.
    // offsetHeight is used (not getBoundingClientRect) so it's reliable regardless of scroll position.
    // On desktop the aside is in a separate column and doesn't affect the scroll offset.
    scrollOffset() {
      if (window.innerWidth < 1024) {
        const aside = this.querySelector('[data-toc-container]');
        if (aside) return this.headerHeight() + aside.offsetHeight;
      }
      return this.headerHeight() + 24;
    }

    // Collect .toc spans, h2s, h3s in DOM order
    collectItems() {
      const body = this.querySelector('[data-article-body]');
      if (!body) return;
      this.tocItems = [];
      body.querySelectorAll('.toc[data-toc-label], h2, h3').forEach(el => {
        if (el.matches('.toc[data-toc-label]')) {
          this.tocItems.push({ el, id: el.id, label: el.dataset.tocLabel, level: 1 });
        } else {
          const label = el.textContent.trim();
          if (!label) { el.hidden = true; return; } // hide & skip empty spacer headings from Shopify editor
          if (!el.id) {
            el.id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          }
          this.tocItems.push({ el, id: el.id, label, level: el.tagName === 'H2' ? 2 : 3 });
        }
      });
    }

    buildTOC() {
      if (!this.tocItems.length) return;
      const container = this.querySelector('[data-toc-container]');
      if (!container) return;

      const tocLabel = container.dataset.tocLabel || 'Table of Contents';

      const linksHTML = this.tocItems.map(({ id, label, level }) =>
        `<a
          href="#${id}"
          data-toc-id="${id}"
          class="toc-nav-link block py-3 text-base text-secondaryDark no-underline hover:opacity-100 transition-colors duration-150"
        >${label}</a>`
      ).join('');

      container.insertAdjacentHTML('afterbegin', `
        <div
          data-toc-wrapper
          class="active mb-8 lg:mb-0 bg-orthoCream rounded-lg overflow-hidden
            [&:not(.active)_[data-accordion-content]]:max-h-0
            [&_[data-accordion-content]]:max-h-[var(--accordion-content-height)]
            [&_[data-accordion-content]]:duration-500
            [&_[data-accordion-content]]:transition-all
            [&_[data-accordion-content]]:ease-out
            [&.active_.rotate]:-rotate-90
            [&_.rotate]:duration-500
            [&_.rotate]:transition-all
            [&_.rotate]:ease-out"
        >
          <button
            data-toc-toggle
            class="w-full flex items-center px-6 py-5 border-b-2 border-primaryLight"
            aria-expanded="true"
          >
            <span class="text-xl font-semibold text-primaryDark">${tocLabel}</span>
            <span aria-hidden="true" class="p-0.5 h-4 w-4 ml-auto flex-shrink-0 relative">
              <span class="rotate block w-0.5 h-full absolute left-1/2 top-0 -translate-x-1/2 bg-midGrey"></span>
              <span class="block w-full h-0.5 absolute top-1/2 left-0 -translate-y-1/2 bg-midGrey"></span>
            </span>
          </button>
          <div
            data-accordion-content
            aria-hidden="false"
            class="p-6"
          >
            <nav class="flex flex-col overflow-y-auto max-h-[267px]">${linksHTML}</nav>
          </div>
        </div>
      `);

      const wrapper = container.querySelector('[data-toc-wrapper]');
      const panel = container.querySelector('[data-accordion-content]');
      const toggle = container.querySelector('[data-toc-toggle]');

      // Sync accordion open/close state to breakpoint — no animation, immediate
      this.onBreakpointChange = () => {
        const isMobile = window.innerWidth < 1024;
        if (isMobile) {
          wrapper.classList.remove('active');
          panel.classList.add('hidden');
          panel.setAttribute('aria-hidden', 'true');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          panel.classList.remove('hidden');
          panel.setAttribute('aria-hidden', 'false');
          toggle.setAttribute('aria-expanded', 'true');
          panel.style.setProperty('--accordion-content-height', `${panel.scrollHeight}px`);
          wrapper.classList.add('active');
        }
      };

      // Set correct initial state, then watch for breakpoint changes
      setTimeout(() => this.onBreakpointChange(), 1);
      this.mql = window.matchMedia('(min-width: 1024px)');
      this.mql.addEventListener('change', this.onBreakpointChange);

      // Toggle button
      toggle.addEventListener('click', () => {
        const isOpen = wrapper.classList.contains('active');
        if (isOpen) {
          this.closePanel(wrapper, panel, toggle);
        } else {
          this.openPanel(wrapper, panel, toggle);
        }
      });

      // Link clicks — scroll to target, close on mobile
      container.querySelectorAll('[data-toc-id]').forEach(link => {
        link.addEventListener('click', e => {
          e.preventDefault();
          const target = document.getElementById(link.dataset.tocId);
          if (target) {
            window.scrollTo({
              top: target.getBoundingClientRect().top + window.scrollY - this.scrollOffset(),
              behavior: 'smooth'
            });
          }
          if (window.innerWidth < 1024) {
            this.closePanel(wrapper, panel, toggle);
          }
        });
      });
    }

    openPanel(wrapper, panel, toggle) {
      panel.classList.remove('hidden');
      panel.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        panel.style.setProperty('--accordion-content-height', `${panel.scrollHeight}px`);
        wrapper.classList.add('active');
      }, 1);
    }

    closePanel(wrapper, panel, toggle) {
      setTimeout(() => {
        panel.style.setProperty('--accordion-content-height', `${panel.scrollHeight}px`);
        wrapper.classList.remove('active');
      }, 1);
      panel.addEventListener('transitionend', () => {
        if (!wrapper.classList.contains('active')) {
          panel.classList.add('hidden');
          panel.setAttribute('aria-hidden', 'true');
          toggle.setAttribute('aria-expanded', 'false');
        }
      }, { once: true });
    }

    initScrollTracking() {
      if (!this.tocItems.length) return;
      this.scrollHandler = () => this.updateActive();
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.updateActive();
    }

    updateActive() {
      const threshold = this.scrollOffset();
      let activeId = this.tocItems[0]?.id ?? null;
      for (let i = this.tocItems.length - 1; i >= 0; i--) {
        if (this.tocItems[i].el.getBoundingClientRect().top <= threshold) {
          activeId = this.tocItems[i].id;
          break;
        }
      }

      if (activeId === this.activeId) return;
      this.activeId = activeId;

      let activeLink = null;
      this.querySelectorAll('[data-toc-id]').forEach(link => {
        const active = link.dataset.tocId === activeId;
        link.classList.toggle('font-medium', active);
        link.classList.toggle('!underline', active);
        link.classList.toggle('text-primaryDark', active);
        link.classList.toggle('opacity-70', !active);
        if (active) activeLink = link;
      });

      // Scroll the active link into view within the TOC nav if it's out of sight
      if (activeLink) activeLink.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });
}
